
import React, { useState } from 'react';
import { Key, Eye, EyeOff, Compass, Crosshair, Globe, Zap, CloudSun, Route, TestTube } from 'lucide-react';
import { RoutingOsrmCalibrationConfig, SystemConfig } from '../../types';
import { runFullComparison, generateComparisonReport, MACEIO_HUB } from '../../services/routingApiComparison';
import { toast } from 'react-hot-toast';

interface Props {
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
}

const DEFAULT_OSRM_CALIBRATION: RoutingOsrmCalibrationConfig = {
  enabled: true,
  shortTripMaxDistanceKm: 3,
  mediumTripMaxDistanceKm: 8,
  shortTripMultiplier: 2.3,
  mediumTripMultiplier: 1.4,
  longTripMultiplier: 1.3,
  fixedDelayMinutes: 1,
  peakHourEnabled: true,
  peakHourMultiplier: 1.1,
  peakMorningStartHour: 7,
  peakMorningEndHour: 9,
  peakEveningStartHour: 17,
  peakEveningEndHour: 19
};

const parseNumberInput = (value: string, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const IntegrationSubTabGoogle: React.FC<Props> = ({ config, setConfig }) => {
  const [showApiKey, setShowApiKey] = useState(false);
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [showHgApiKey, setShowHgApiKey] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<string | null>(null);
  const routingProvider = config.routingApiProvider ?? 'osrm';
  const fallbackEnabled = config.routingApiFallbackEnabled ?? true;
  const calibration = { ...DEFAULT_OSRM_CALIBRATION, ...(config.routingOsrmCalibration ?? {}) };

  const setCalibration = (patch: Partial<RoutingOsrmCalibrationConfig>) => {
    setConfig({
      ...config,
      routingOsrmCalibration: { ...calibration, ...patch }
    });
  };

  const SectionHeader = ({ icon: Icon, title, subTitle, color = 'text-status-info' }: any) => (
    <div className="flex items-center gap-3 border-b border-border-dim pb-4 mb-6">
      <div className={`p-2.5 bg-card-bg border border-border-dim rounded-xl ${color}`}><Icon className="w-5 h-5" /></div>
      <div><h4 className="text-base font-black text-text-primary uppercase tracking-widest">{title}</h4><p className="text-xs text-text-muted font-bold uppercase tracking-tighter">{subTitle}</p></div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in slide-in-from-left-4 duration-500">
      <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl relative overflow-hidden group">
        <SectionHeader icon={Key} title="Chaves de Acesso" subTitle="Credenciais de autenticação Cloud & AI" color="text-status-info" />
        <div className="space-y-5 relative z-10">
          <div className="space-y-2">
            <label htmlFor="googleMapsApiKey" className="text-xs font-black text-text-muted uppercase tracking-widest ml-1 flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> Google Maps API Key</label>
            <div className="relative">
              <input
                id="googleMapsApiKey"
                type={showApiKey ? "text" : "password"}
                value={config.googleMapsApiKey || ''}
                onChange={(e) => setConfig({ ...config, googleMapsApiKey: e.target.value })}
                className="w-full bg-input-bg border border-input-border rounded-2xl pl-5 pr-12 py-4 text-base text-status-info font-mono focus:border-status-info/50 outline-none transition-all"
              />
              <button type="button" onClick={() => setShowApiKey(!showApiKey)} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary">
                {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="geminiApiKey" className="text-xs font-black text-text-muted uppercase tracking-widest ml-1 flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-sal-cyber-teal" /> Google Gemini API Key</label>
            <div className="relative">
              <input
                id="geminiApiKey"
                type={showGeminiKey ? "text" : "password"}
                value={config.geminiApiKey || ''}
                onChange={(e) => setConfig({ ...config, geminiApiKey: e.target.value })}
                className="w-full bg-input-bg border border-input-border rounded-2xl pl-5 pr-12 py-4 text-base text-sal-cyber-teal font-mono focus:border-sal-cyber-teal/50 outline-none transition-all"
              />
              <button type="button" onClick={() => setShowGeminiKey(!showGeminiKey)} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary">
                {showGeminiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="hgWeatherApiKey" className="text-xs font-black text-text-muted uppercase tracking-widest ml-1 flex items-center gap-2"><CloudSun className="w-3.5 h-3.5 text-status-pending" /> HG Weather API Key</label>
            <div className="relative">
              <input
                id="hgWeatherApiKey"
                type={showHgApiKey ? "text" : "password"}
                value={config.hgWeatherApiKey || ''}
                onChange={(e) => setConfig({ ...config, hgWeatherApiKey: e.target.value })}
                className="w-full bg-input-bg border border-input-border rounded-2xl pl-5 pr-12 py-4 text-base text-status-pending font-mono focus:border-status-pending/50 outline-none transition-all"
              />
              <button type="button" onClick={() => setShowHgApiKey(!showHgApiKey)} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary">
                {showHgApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl relative overflow-hidden group">
        <SectionHeader icon={Route} title="API de Roteamento" subTitle="Escolha entre API gratuita (OSRM) ou paga (Google)" color="text-status-ready" />

        <div className="bg-bg-main/50 p-5 rounded-2xl border border-border-dim space-y-4 mb-6">
          <p className="text-base text-text-primary font-bold">🗺️ Provedor de Roteamento</p>
          <p className="text-sm text-text-muted">
            O sistema pode usar <strong>OSRM (gratuito)</strong> ou <strong>Google Maps (pago)</strong> para calcular rotas.
            Google so entra se for selecionado manualmente ou como contingência quando o OSRM falhar por completo.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <button
              type="button"
              onClick={() => setConfig({ ...config, routingApiProvider: 'here' })}
              className={`p-4 rounded-2xl border-2 transition-all text-left ${routingProvider === 'here' ? 'border-sal-cyber-teal bg-sal-cyber-teal/10' : 'border-border-dim hover:border-sal-cyber-teal/30'}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 rounded-full border-2 border-sal-cyber-teal flex items-center justify-center">
                  {routingProvider === 'here' && <div className="w-2 h-2 rounded-full bg-sal-cyber-teal" />}
                </div>
                <span className="font-black text-base text-text-primary">🎯 HERE Maps (Rec.)</span>
              </div>
              <p className="text-xs text-text-muted">Equilíbrio perfeito entre custo e precisão. Recomendado.</p>
            </button>

            <button
              type="button"
              onClick={() => setConfig({ ...config, routingApiProvider: 'google' })}
              className={`p-4 rounded-2xl border-2 transition-all text-left ${routingProvider === 'google' ? 'border-status-info bg-status-info/10' : 'border-border-dim hover:border-status-info/30'}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 rounded-full border-2 border-status-info flex items-center justify-center">
                  {routingProvider === 'google' && <div className="w-2 h-2 rounded-full bg-status-info" />}
                </div>
                <span className="font-black text-base text-text-primary">💰 Google Maps</span>
              </div>
              <p className="text-xs text-text-muted">Máxima precisão com trânsito em tempo real. Requer API Key.</p>
            </button>

            <button
              type="button"
              onClick={() => setConfig({ ...config, routingApiProvider: 'osrm' })}
              className={`p-4 rounded-2xl border-2 transition-all text-left ${routingProvider === 'osrm' ? 'border-status-ready bg-status-ready/10' : 'border-border-dim hover:border-status-ready/30'}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 rounded-full border-2 border-status-ready flex items-center justify-center">
                  {routingProvider === 'osrm' && <div className="w-2 h-2 rounded-full bg-status-ready" />}
                </div>
                <span className="font-black text-base text-text-primary">🆓 OSRM (Gratuito)</span>
              </div>
              <p className="text-xs text-text-muted">Open Source Routing Machine. Precisão média-alta sem custos.</p>
            </button>
          </div>

          <div className="flex items-center gap-3 mt-4 p-3 bg-card-bg rounded-xl border border-border-dim">
            <input
              type="checkbox"
              id="fallbackEnabled"
              checked={fallbackEnabled}
              onChange={(e) => {
                setConfig({ ...config, routingApiFallbackEnabled: e.target.checked });
              }}
              className="w-4 h-4 accent-sal-cyber-teal"
            />
            <label htmlFor="fallbackEnabled" className="text-sm text-text-primary font-bold cursor-pointer">
              Habilitar Fallback Automático
            </label>
            <span className="text-xs text-text-muted ml-auto">
              {routingProvider === 'osrm' ? 'Google somente se OSRM falhar' : 'OSRM somente se Google falhar'}
            </span>
          </div>

          <div className="mt-4 p-4 bg-card-bg rounded-xl border border-border-dim space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-black text-text-primary uppercase tracking-wider">Calibração de Tempo OSRM</p>
                <p className="text-xs text-text-muted">Ajuste manual para aproximar Previsão de Chegada do OSRM com a realidade local.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="osrmCalibrationEnabled"
                  checked={calibration.enabled}
                  onChange={(e) => setCalibration({ enabled: e.target.checked })}
                  className="w-4 h-4 accent-status-ready"
                />
                <label htmlFor="osrmCalibrationEnabled" className="text-xs font-black uppercase tracking-wider text-text-primary cursor-pointer">Ativo</label>
              </div>
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${!calibration.enabled ? 'opacity-60 pointer-events-none' : ''}`}>
              <div className="space-y-1">
                <label htmlFor="shortTripMaxDistanceKm" className="text-xs font-black uppercase tracking-wider text-text-muted">Curta até (km)</label>
                <input
                  id="shortTripMaxDistanceKm"
                  type="number"
                  min={0.5}
                  max={15}
                  step={0.1}
                  value={calibration.shortTripMaxDistanceKm}
                  onChange={(e) => setCalibration({ shortTripMaxDistanceKm: parseNumberInput(e.target.value, calibration.shortTripMaxDistanceKm) })}
                  className="w-full bg-input-bg border border-input-border rounded-xl px-3 py-2 text-sm text-text-primary"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="mediumTripMaxDistanceKm" className="text-xs font-black uppercase tracking-wider text-text-muted">Media ate (km)</label>
                <input
                  id="mediumTripMaxDistanceKm"
                  type="number"
                  min={0.5}
                  max={30}
                  step={0.1}
                  value={calibration.mediumTripMaxDistanceKm}
                  onChange={(e) => setCalibration({ mediumTripMaxDistanceKm: parseNumberInput(e.target.value, calibration.mediumTripMaxDistanceKm) })}
                  className="w-full bg-input-bg border border-input-border rounded-xl px-3 py-2 text-sm text-text-primary"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="shortTripMultiplier" className="text-xs font-black uppercase tracking-wider text-text-muted">Multiplicador curta</label>
                <input
                  id="shortTripMultiplier"
                  type="number"
                  min={0.8}
                  max={3.5}
                  step={0.05}
                  value={calibration.shortTripMultiplier}
                  onChange={(e) => setCalibration({ shortTripMultiplier: parseNumberInput(e.target.value, calibration.shortTripMultiplier) })}
                  className="w-full bg-input-bg border border-input-border rounded-xl px-3 py-2 text-sm text-text-primary"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="mediumTripMultiplier" className="text-xs font-black uppercase tracking-wider text-text-muted">Multiplicador media</label>
                <input
                  id="mediumTripMultiplier"
                  type="number"
                  min={0.8}
                  max={3.5}
                  step={0.05}
                  value={calibration.mediumTripMultiplier}
                  onChange={(e) => setCalibration({ mediumTripMultiplier: parseNumberInput(e.target.value, calibration.mediumTripMultiplier) })}
                  className="w-full bg-input-bg border border-input-border rounded-xl px-3 py-2 text-sm text-text-primary"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="longTripMultiplier" className="text-xs font-black uppercase tracking-wider text-text-muted">Multiplicador longa</label>
                <input
                  id="longTripMultiplier"
                  type="number"
                  min={0.8}
                  max={3.5}
                  step={0.05}
                  value={calibration.longTripMultiplier}
                  onChange={(e) => setCalibration({ longTripMultiplier: parseNumberInput(e.target.value, calibration.longTripMultiplier) })}
                  className="w-full bg-input-bg border border-input-border rounded-xl px-3 py-2 text-sm text-text-primary"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="fixedDelayMinutes" className="text-xs font-black uppercase tracking-wider text-text-muted">Atraso fixo (min)</label>
                <input
                  id="fixedDelayMinutes"
                  type="number"
                  min={0}
                  max={20}
                  step={0.5}
                  value={calibration.fixedDelayMinutes}
                  onChange={(e) => setCalibration({ fixedDelayMinutes: parseNumberInput(e.target.value, calibration.fixedDelayMinutes) })}
                  className="w-full bg-input-bg border border-input-border rounded-xl px-3 py-2 text-sm text-text-primary"
                />
              </div>
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${!calibration.enabled ? 'opacity-60 pointer-events-none' : ''}`}>
              <div className="flex items-center gap-2 p-3 bg-bg-main/40 border border-border-dim rounded-xl">
                <input
                  type="checkbox"
                  id="peakHourEnabled"
                  checked={calibration.peakHourEnabled}
                  onChange={(e) => setCalibration({ peakHourEnabled: e.target.checked })}
                  className="w-4 h-4 accent-status-ready"
                />
                <label htmlFor="peakHourEnabled" className="text-xs font-black uppercase tracking-wider text-text-primary cursor-pointer">Hora de pico</label>
              </div>

              <div className="space-y-1">
                <label htmlFor="peakHourMultiplier" className="text-xs font-black uppercase tracking-wider text-text-muted">Multiplicador pico</label>
                <input
                  id="peakHourMultiplier"
                  type="number"
                  min={1}
                  max={2.5}
                  step={0.05}
                  value={calibration.peakHourMultiplier}
                  onChange={(e) => setCalibration({ peakHourMultiplier: parseNumberInput(e.target.value, calibration.peakHourMultiplier) })}
                  className="w-full bg-input-bg border border-input-border rounded-xl px-3 py-2 text-sm text-text-primary"
                />
              </div>

              <div className="space-y-1">
                <span className="text-xs font-black uppercase tracking-wider text-text-muted block">Janelas (h)</span>
                <div className="grid grid-cols-4 gap-1">
                  <input
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    value={calibration.peakMorningStartHour}
                    onChange={(e) => setCalibration({ peakMorningStartHour: parseNumberInput(e.target.value, calibration.peakMorningStartHour) })}
                    className="bg-input-bg border border-input-border rounded-xl px-2 py-2 text-sm text-text-primary"
                  />
                  <input
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    value={calibration.peakMorningEndHour}
                    onChange={(e) => setCalibration({ peakMorningEndHour: parseNumberInput(e.target.value, calibration.peakMorningEndHour) })}
                    className="bg-input-bg border border-input-border rounded-xl px-2 py-2 text-sm text-text-primary"
                  />
                  <input
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    value={calibration.peakEveningStartHour}
                    onChange={(e) => setCalibration({ peakEveningStartHour: parseNumberInput(e.target.value, calibration.peakEveningStartHour) })}
                    className="bg-input-bg border border-input-border rounded-xl px-2 py-2 text-sm text-text-primary"
                  />
                  <input
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    value={calibration.peakEveningEndHour}
                    onChange={(e) => setCalibration({ peakEveningEndHour: parseNumberInput(e.target.value, calibration.peakEveningEndHour) })}
                    className="bg-input-bg border border-input-border rounded-xl px-2 py-2 text-sm text-text-primary"
                  />
                </div>
              </div>
            </div>
          </div>
           
          {/* Debug Info */}
          <div className="mt-4 p-3 bg-black/30 rounded-xl border border-border-dim text-xs font-mono text-text-muted">
            <p className="font-bold text-text-secondary mb-1">Debug Info:</p>
            <p>Provider: {routingProvider}</p>
            <p>Fallback: {fallbackEnabled.toString()}</p>
            <p>OSRM Calibration: {calibration.enabled ? 'ON' : 'OFF'}</p>
            <p>API Key: {config.googleMapsApiKey ? '✓ Configurada (' + config.googleMapsApiKey.length + ' chars)' : '✗ Não configurada'}</p>
          </div>
        </div>

        {/* Ferramenta de Comparação */}
        <div className="bg-bg-main/50 p-5 rounded-2xl border border-border-dim space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-base text-text-primary font-bold flex items-center gap-2">
              <TestTube className="w-4 h-4" /> Teste de Precisão (Maceió)
            </p>
            <button
              type="button"
              onClick={async () => {
                setIsComparing(true);
                setComparisonResults(null);
                toast.loading('Comparando APIs...', { id: 'routing-compare' });
                
                try {
                  const results = await runFullComparison(config.googleMapsApiKey, calibration);
                  const report = generateComparisonReport(results);
                  
                  setComparisonResults(report);
                  toast.success('Comparação concluída!', { id: 'routing-compare' });
                } catch (e: any) {
                  console.error('[Comparison] Erro:', e);
                  toast.error(`Erro na comparação: ${e.message}`, { id: 'routing-compare' });
                } finally {
                  setIsComparing(false);
                }
              }}
              disabled={isComparing}
              className="px-4 py-2 bg-sal-cyber-teal text-white text-xs font-black uppercase tracking-widest rounded-xl hover:brightness-110 transition-all disabled:opacity-50"
            >
              {isComparing ? 'Comparando...' : 'Executar Teste'}
            </button>
          </div>
          <p className="text-sm text-text-muted">
            Compara rotas do Hub ({MACEIO_HUB.address}) para 8 destinos em Maceió.
            Ajuda a decidir qual API usar baseado em precisão real.
          </p>

          {/* Validation Message */}
          {!config.googleMapsApiKey && (
            <div className="p-3 bg-status-error/10 border border-status-error/30 rounded-xl">
              <p className="text-sm text-status-error font-bold flex items-center gap-2">
                ⚠️ API Key do Google Maps não configurada
              </p>
              <p className="text-xs text-text-muted mt-1">
                A comparação usará apenas OSRM. Para comparar com Google Maps, adicione sua API Key acima.
              </p>
            </div>
          )}

          {comparisonResults && (
            <div className="mt-4 p-4 bg-black/50 rounded-xl border border-border-dim font-mono text-xs text-text-muted whitespace-pre-wrap max-h-96 overflow-y-auto">
              {comparisonResults}
            </div>
          )}
        </div>
      </section>

      <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl relative overflow-hidden group">
        <SectionHeader icon={Zap} title="S.A.L. Recon" subTitle="Visão tática de rua" color="text-sal-cyber-teal" />

        {/* Bloco de explicação para SAL Recon */}
        <div className="bg-bg-main/50 p-5 rounded-2xl border border-border-dim space-y-2 mb-6">
          <p className="text-base text-text-primary font-bold">O que é S.A.L. Recon?</p>
          <p className="text-sm text-text-muted">O S.A.L. Recon fornece uma visão tática das ruas e arredores do seu Hub Logístico usando o Street View do Google Maps. Isso ajuda na tomada de decisão em tempo real e na verificação visual de endereços.</p>
          <p className="text-sm text-text-muted">Ajuste o ângulo e o raio de visão para otimizar sua perspectiva tática.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
          <div className="space-y-4 bg-bg-main/30 p-5 rounded-2xl border border-border-dim">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-status-info" />
                <span className="text-xs font-black text-status-info uppercase tracking-widest">Ângulo (Heading)</span>
              </div>
              <span className="text-base font-mono font-black text-text-primary">{config.streetViewHeading || 235}º</span>
            </div>
            <input
              type="range" min="0" max="360" step="5"
              value={config.streetViewHeading || 235}
              onChange={(e) => setConfig({ ...config, streetViewHeading: parseInt(e.target.value) })}
              className="w-full h-1.5 bg-border-strong rounded-lg appearance-none cursor-pointer accent-sal-cyber-teal"
            />
            <p className="text-xs text-text-muted mt-2 font-bold uppercase tracking-tight">Define a direção padrão da câmera.</p>
          </div>
          <div className="space-y-4 bg-bg-main/30 p-5 rounded-2xl border border-border-dim">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-center gap-2">
                <Crosshair className="w-4 h-4 text-sal-cyber-teal" />
                <span className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest">Raio (Radius)</span>
              </div>
              <span className="text-base font-mono font-black text-text-primary">{config.streetViewRadius || 30}m</span>
            </div>
            <input
              type="range" min="10" max="100" step="5"
              value={config.streetViewRadius || 30}
              onChange={(e) => setConfig({ ...config, streetViewRadius: parseInt(e.target.value) })}
              className="w-full h-1.5 bg-border-strong rounded-lg appearance-none cursor-pointer accent-sal-cyber-teal"
            />
            <p className="text-xs text-text-muted mt-2 font-bold uppercase tracking-tight">Define a precisão da busca visual.</p>
          </div>
        </div>
      </section>
    </div>
  );
};
